import { ExternalLink, Shield, Scale } from "lucide-react";
import Seo from "../../components/Seo";

const IABS_URL =
  "https://www.har.com/mhf/terms/dispBrokerInfo?sitetype=aws&cid=537207";

/**
 * TREC Information About Brokerage Services (IABS Form 2501)
 * HAR blocks iframe embeds with bot-detection, so we render a branded landing
 * card that opens the official HAR-hosted form (with Pierre's live TREC data)
 * in a new tab. Meets the display requirement per TREC §1101.558.
 */
export default function Iabs() {
  return (
    <div className="bg-stone-50 min-h-screen" data-testid="iabs-page">
      <Seo
        title="Information About Brokerage Services (IABS) — Texas TREC"
        description="Official Texas Real Estate Commission Information About Brokerage Services form for Gens Realty, LLC and Pierre Joseph (License #0537207)."
        path="/iabs"
      />

      <section className="max-w-4xl mx-auto px-6 lg:px-10 pt-14 pb-24">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#E63946] mb-3">
          TREC Form IABS · Official
        </div>
        <h1 className="font-display text-4xl lg:text-5xl font-light tracking-tighter text-[#1A1A1A]">
          Information About
          <br />
          <em className="not-italic text-[#E63946]">Brokerage Services</em>
        </h1>
        <p className="text-[#555] mt-4 max-w-2xl text-lg">
          Texas law requires all real estate license holders to give the following information
          about brokerage services to prospective buyers, tenants, sellers and landlords.
        </p>

        <div className="mt-10 bg-white border border-gray-200 rounded-2xl p-8 lg:p-10">
          <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-4">
            Brokerage on file with TREC
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <FieldRow label="Broker / License Holder" value="Pierre Joseph" />
            <FieldRow label="License No." value="#0537207" />
            <FieldRow label="Brokerage" value="Gens Realty, LLC" />
            <FieldRow label="NMLS #" value="1978260 (Edge Home Finance)" />
            <FieldRow label="Phone" value="(832) 741-4662" />
            <FieldRow label="Email" value="PJoseph@gensrealty.com" />
            <div className="md:col-span-2">
              <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500">Address</div>
              <div className="text-[#1A1A1A] font-medium mt-0.5">
                14911 Heritage Wood Drive · Houston, TX 77082
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href={IABS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 bg-[#E63946] hover:bg-[#c9313d] text-white text-center px-6 py-4 rounded-md font-semibold text-sm flex items-center justify-center gap-2"
              data-testid="iabs-open-official"
            >
              Open Official TREC IABS Form <ExternalLink className="w-4 h-4" />
            </a>
            <a
              href="/consumer-protection"
              className="flex-1 bg-[#1A1A1A] hover:bg-[#333] text-white text-center px-6 py-4 rounded-md font-semibold text-sm flex items-center justify-center gap-2"
              data-testid="iabs-view-cpn"
            >
              Consumer Protection Notice
            </a>
          </div>
          <p className="text-[10px] text-gray-500 mt-4 text-center">
            The official form is hosted by the Houston Association of REALTORS® and reflects the
            live TREC-verified data on file for Pierre Joseph.
          </p>
        </div>

        {/* Summary of broker duties — TREC-mandated language */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <Shield className="w-5 h-5 text-[#E63946] mb-3" />
            <div className="font-display text-lg font-semibold mb-2">Broker&apos;s minimum duties</div>
            <ul className="text-sm text-[#333] space-y-1.5 leading-relaxed list-disc pl-5">
              <li>Put your interests above all others.</li>
              <li>Inform you of any material information about the property or transaction.</li>
              <li>Answer your questions and present any offer or counter-offer.</li>
              <li>Treat all parties honestly and fairly.</li>
            </ul>
          </div>
          <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <Scale className="w-5 h-5 text-[#E63946] mb-3" />
            <div className="font-display text-lg font-semibold mb-2">Written agreements</div>
            <p className="text-sm text-[#333] leading-relaxed">
              To avoid disputes, all agreements between you and a broker should be in writing and
              clearly establish the broker&apos;s duties and how the broker will be paid. See the
              official IABS form for the complete explanation of each representation type
              (owner&apos;s agent, buyer&apos;s agent, intermediary, subagent).
            </p>
          </div>
        </div>

        <div className="mt-8 text-[11px] leading-relaxed text-gray-500">
          <p>
            Regulated by the Texas Real Estate Commission ·{" "}
            <a
              href="https://www.trec.texas.gov"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[#E63946]"
            >
              www.trec.texas.gov
            </a>{" "}
            · IABS 1-0
          </p>
        </div>
      </section>
    </div>
  );
}

function FieldRow({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500">{label}</div>
      <div className="text-[#1A1A1A] font-medium mt-0.5">{value}</div>
    </div>
  );
}
